import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import {
  ArrowRight,
  CheckCircle2,
  MessageCircle,
  Quote,
  Sparkles,
  Stethoscope,
  ShieldCheck,
  Clock,
  Zap,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { KENTRO } from "@/lib/constants";
import {
  CLINICAS_VERTICALS,
  CLINICAS_COMMON_PROBLEMS,
  CLINICAS_FEATURES,
  CLINICAS_FAQ,
  type ClinicaVertical,
} from "@/lib/clinicas-data";
import { trackIaNav, trackEvent, trackMetaPixel, trackMetaPixelCustom, withUtm } from "@/lib/analytics";

const SITE = "https://kentro.com.br";
const URL = `${SITE}/clinicas`;
const TITLE =
  "Kentro para Clínicas — Médicas, Odontológicas e Estéticas com IA e WhatsApp Oficial";
const DESCRIPTION =
  "Menos no-show, mais agendamentos e orçamentos fechados. Plataforma omnichannel com IA Dr. treinada para clínicas médicas, odontológicas e estéticas — WhatsApp Oficial, CRM e recall automático.";
const IA_URL = "https://formulario.kentro.com.br/medico";

export const Route = createFileRoute("/clinicas/")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { name: "keywords", content: "clínicas médicas, clínicas odontológicas, clínicas estéticas, whatsapp para clínica, ia para clínica, crm para clínica, no-show, agendamento online, kentro" },
      { property: "og:type", content: "website" },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: URL },
      { property: "og:image", content: `${SITE}/og/og-default.png` },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
      { name: "twitter:image", content: `${SITE}/og/og-default.png` },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: "Plataforma de atendimento omnichannel com IA para clínicas",
          provider: { "@type": "Organization", name: "Kentro Sistemas", url: SITE },
          areaServed: "BR",
          audience: {
            "@type": "Audience",
            audienceType: "Clínicas médicas, odontológicas e estéticas",
          },
          name: TITLE,
          description: DESCRIPTION,
          url: URL,
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: CLINICAS_FAQ.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
            { "@type": "ListItem", position: 2, name: "Clínicas", item: URL },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "@id": `${URL}#localbusiness`,
          name: "Kentro Sistemas",
          description: DESCRIPTION,
          url: URL,
          image: `${SITE}/og/og-default.png`,
          logo: `${SITE}/icon-512.png`,
          telephone: "+5508005502020",
          email: KENTRO.email,
          priceRange: "$$",
          address: {
            "@type": "PostalAddress",
            streetAddress: "Alameda Rio Negro, 503, Sala 2020",
            addressLocality: "Barueri",
            addressRegion: "SP",
            postalCode: "06454-000",
            addressCountry: "BR",
          },
          areaServed: [
            { "@type": "Country", name: "Brasil" },
          ],
          geo: {
            "@type": "GeoCoordinates",
            latitude: -23.4989,
            longitude: -46.8474,
          },
          openingHoursSpecification: [
            {
              "@type": "OpeningHoursSpecification",
              dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
              opens: "08:00",
              closes: "18:00",
            },
          ],
          sameAs: [
            "https://www.instagram.com/kentrosistemas",
            "https://www.linkedin.com/company/kentro-sistemas",
          ],
          contactPoint: [
            {
              "@type": "ContactPoint",
              telephone: "+5508005502020",
              contactType: "customer service",
              areaServed: "BR",
              availableLanguage: ["Portuguese"],
            },
            {
              "@type": "ContactPoint",
              telephone: "+5511915827716",
              contactType: "sales",
              areaServed: "BR",
              availableLanguage: ["Portuguese"],
            },
          ],
          makesOffer: CLINICAS_VERTICALS.map((v: ClinicaVertical) => ({
            "@type": "Offer",
            name: `Kentro para ${v.name}`,
            category: "Software as a Service",
          })),
        }),
      },
    ],
  }),
  component: ClinicasPage,
});

function ClinicasPage() {
  return (
    <>
      <Hero />
      <TrustStrip />
      <Verticals />
      <CommonProblems />
      <IaHighlight />
      <Solution />
      <Metrics />
      <Testimonials />
      <Faq />
      <FinalCta />
    </>
  );
}

/* ---------------- HERO ---------------- */
function Hero() {
  return (
    <section className="relative overflow-hidden border-b border-white/5 py-20 md:py-28">
      <div className="absolute inset-0 -z-10 opacity-40" style={{ background: "var(--gradient-hero)" }} />
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="mb-4 flex items-center gap-2 text-sm text-muted-foreground">
          <Link to="/" className="hover:text-foreground">Home</Link>
          <span>/</span>
          <span className="text-foreground">Clínicas</span>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <span className="gradient-primary inline-flex size-14 items-center justify-center rounded-2xl text-white shadow-glow">
            <Stethoscope className="size-7" />
          </span>
          <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Landing · Saúde
          </span>
        </div>

        <h1 className="mt-6 max-w-4xl text-4xl font-bold leading-tight md:text-5xl lg:text-6xl" style={{ fontFamily: "var(--font-display)" }}>
          A plataforma que faz sua <span className="gradient-text">clínica</span> parar de perder paciente por falta de resposta.
        </h1>
        <p className="mt-5 max-w-2xl text-lg text-muted-foreground">
          Clínicas <strong className="text-foreground">médicas, odontológicas e estéticas</strong> usam a Kentro
          para reduzir no-show, atender no WhatsApp Oficial 24h com IA e fechar mais orçamentos —
          sem contratar mais recepção.
        </p>

        <div className="mt-8 flex flex-wrap gap-3">
          <Button asChild size="lg" className="gradient-primary text-white shadow-glow">
            <Link
              to="/contato"
              search={{ src: "clinicas" }}
              onClick={() => {
                trackEvent("demo_cta_click", {
                  location: "clinicas_hero",
                  page: "clinicas",
                  target: "form_contato",
                });
                trackMetaPixelCustom("ScheduleDemo", {
                  source: "clinicas",
                  location: "clinicas_hero",
                  page: "clinicas",
                });
              }}
            >
              Agendar demonstração <ArrowRight className="ml-1 size-4" />
            </Link>
          </Button>
          <Button asChild size="lg" variant="outline">
            <a
              href={withUtm(IA_URL, {
                utm_source: "site_kentro",
                utm_medium: "landing_clinicas",
                utm_campaign: "experimente_ia_dr",
                utm_content: "hero",
              })}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                trackIaNav("clinicas_hero", "clinicas", { ia_slug: "medico" });
                trackEvent("ia_try_click", { location: "clinicas_hero", ia_slug: "medico" });
              }}
            >
              <Sparkles className="mr-1 size-4" /> Testar IA Dr. no WhatsApp
            </a>
          </Button>
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
          <span className="inline-flex items-center gap-1.5"><ShieldCheck className="size-4 text-primary-glow" /> WhatsApp Oficial (Meta)</span>
          <span className="inline-flex items-center gap-1.5"><Clock className="size-4 text-primary-glow" /> IA 24/7</span>
          <span className="inline-flex items-center gap-1.5"><Zap className="size-4 text-primary-glow" /> Onboarding em 7–15 dias</span>
        </div>
      </div>
    </section>
  );
}

/* ---------------- TRUST ---------------- */
function TrustStrip() {
  const items = [
    { v: "-42%", l: "no-show médio em clínicas Kentro" },
    { v: "+31%", l: "conversão em avaliações e orçamentos" },
    { v: "38%", l: "dos contatos chegam fora do horário" },
    { v: "24/7", l: "cobertura com IA Dr." },
  ];
  return (
    <section className="border-b border-white/5 bg-white/[0.02] py-10">
      <div className="mx-auto grid max-w-6xl grid-cols-2 gap-6 px-4 sm:px-6 md:grid-cols-4 lg:px-8">
        {items.map((it) => (
          <div key={it.l} className="text-center md:text-left">
            <div className="gradient-text text-2xl font-bold md:text-3xl" style={{ fontFamily: "var(--font-display)" }}>{it.v}</div>
            <div className="mt-1 text-xs text-muted-foreground md:text-sm">{it.l}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ---------------- VERTICALS ---------------- */
function Verticals() {
  return (
    <section id="verticais" className="py-16 md:py-24">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <div className="text-sm font-semibold uppercase tracking-wide text-primary-glow">Feita para 3 mundos</div>
          <h2 className="mt-2 text-3xl font-bold md:text-4xl" style={{ fontFamily: "var(--font-display)" }}>
            Uma plataforma. <span className="gradient-text">Três realidades de clínica.</span>
          </h2>
          <p className="mt-3 text-muted-foreground">
            A operação muda muito entre médica, odontológica e estética. A Kentro entrega um núcleo comum
            (omnichannel + IA + CRM) e um playbook específico para cada uma.
          </p>
        </div>

        <div className="mt-10 grid gap-6 lg:grid-cols-3">
          {CLINICAS_VERTICALS.map((v, i) => (
            <VerticalCard key={v.slug} v={v} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function VerticalCard({ v, i }: { v: ClinicaVertical; i: number }) {
  const Icon = v.icon;
  return (
    <motion.article
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: i * 0.05 }}
      className="glass-card flex h-full flex-col rounded-2xl p-6 md:p-7"
    >
      <div className="flex items-center gap-3">
        <span className="gradient-primary inline-flex size-11 items-center justify-center rounded-xl text-white shadow-glow">
          <Icon className="size-5" />
        </span>
        <div>
          <h3 className="text-lg font-bold" style={{ fontFamily: "var(--font-display)" }}>{v.name}</h3>
          <p className="text-xs text-muted-foreground">{v.tagline}</p>
        </div>
      </div>

      <div className="mt-5">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-red-400/90">Dores</div>
        <ul className="mt-2 space-y-1.5 text-sm text-muted-foreground">
          {v.pains.map((p) => (
            <li key={p} className="flex gap-2"><span className="mt-1.5 size-1 shrink-0 rounded-full bg-red-400/70" />{p}</li>
          ))}
        </ul>
      </div>

      <div className="mt-5">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-primary-glow">O que a Kentro entrega</div>
        <ul className="mt-2 space-y-1.5 text-sm">
          {v.wins.map((w) => (
            <li key={w} className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 size-4 shrink-0 text-primary-glow" />{w}</li>
          ))}
        </ul>
      </div>

      <div className="mt-6 grid grid-cols-3 gap-2 border-t border-white/5 pt-5">
        {v.metrics.map((m) => (
          <div key={m.label} className="text-center">
            <div className="gradient-text text-lg font-bold" style={{ fontFamily: "var(--font-display)" }}>{m.value}</div>
            <div className="mt-1 text-[10px] leading-tight text-muted-foreground">{m.label}</div>
          </div>
        ))}
      </div>

      <figure className="mt-6 rounded-xl border border-white/5 bg-white/[0.02] p-4">
        <Quote className="size-4 text-primary-glow/80" />
        <blockquote className="mt-2 text-sm leading-relaxed text-foreground/90">"{v.testimonial.quote}"</blockquote>
        <figcaption className="mt-3 text-xs text-muted-foreground">
          <span className="font-semibold text-foreground">{v.testimonial.author}</span> · {v.testimonial.role}
        </figcaption>
      </figure>

      <div className="mt-5">
        <Link
          to="/clinicas/$vertical"
          params={{ vertical: v.slug }}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary-glow hover:underline"
        >
          Ver página de {v.shortName.toLowerCase()} <ArrowRight className="size-4" />
        </Link>
      </div>
    </motion.article>
  );
}

/* ---------------- PROBLEMS ---------------- */
function CommonProblems() {
  return (
    <section className="border-y border-white/5 bg-white/[0.02] py-16 md:py-20">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <div className="text-sm font-semibold uppercase tracking-wide text-red-400/90">O que trava a agenda</div>
          <h2 className="mt-2 text-3xl font-bold md:text-4xl" style={{ fontFamily: "var(--font-display)" }}>
            Quatro problemas que <span className="gradient-text">custam caro</span> em qualquer clínica.
          </h2>
        </div>
        <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {CLINICAS_COMMON_PROBLEMS.map((p, i) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.35, delay: i * 0.05 }}
              className="glass-card rounded-2xl p-5"
            >
              <div className="text-xs font-semibold text-red-400/90">#{i + 1}</div>
              <h3 className="mt-2 font-bold">{p.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{p.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- IA HIGHLIGHT ---------------- */
function IaHighlight() {
  return (
    <section className="py-16 md:py-20">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
          className="relative overflow-hidden rounded-3xl border border-primary-glow/20 bg-gradient-to-br from-primary/15 via-background to-primary-glow/10 p-6 md:p-10"
        >
          <div className="absolute -right-16 -top-16 size-56 rounded-full bg-primary/30 blur-3xl" aria-hidden />
          <div className="relative grid items-center gap-6 md:grid-cols-[1fr_auto]">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-primary-glow/30 bg-background/40 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-primary-glow">
                <Sparkles className="size-3.5" /> Experimente agora · IA Dr.
              </span>
              <h2 className="mt-3 text-2xl font-bold md:text-3xl" style={{ fontFamily: "var(--font-display)" }}>
                Converse com a IA Dr. no WhatsApp em menos de 30 segundos.
              </h2>
              <p className="mt-3 max-w-xl text-sm text-muted-foreground md:text-base">
                Treinada para clínicas médicas, odontológicas e estéticas. Faça perguntas reais de paciente
                — preparo de exame, valores de procedimentos, planos, horários — e veja como ela responde.
              </p>
            </div>
            <div className="flex flex-wrap gap-2 md:self-center">
              <Button asChild className="gradient-primary text-white shadow-glow">
                <a
                  href={withUtm(IA_URL, {
                    utm_source: "site_kentro",
                    utm_medium: "landing_clinicas",
                    utm_campaign: "experimente_ia_dr",
                    utm_content: "ia_block",
                  })}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackIaNav("clinicas_ia_block", "clinicas", { ia_slug: "medico" })}
                >
                  Experimente agora <ArrowRight className="ml-1 size-4" />
                </a>
              </Button>
              <Button asChild variant="outline">
                <Link to="/ia">Ver todas as IAs</Link>
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ---------------- SOLUTION ---------------- */
function Solution() {
  return (
    <section className="border-y border-white/5 bg-white/[0.02] py-16 md:py-24">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <div className="text-sm font-semibold uppercase tracking-wide text-primary-glow">A solução Kentro</div>
          <h2 className="mt-2 text-3xl font-bold md:text-4xl" style={{ fontFamily: "var(--font-display)" }}>
            Tudo que sua clínica precisa <span className="gradient-text">em um único painel.</span>
          </h2>
        </div>
        <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {CLINICAS_FEATURES.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.35, delay: i * 0.04 }}
              className="glass-card rounded-2xl p-6"
            >
              <div className="gradient-primary mb-3 inline-flex size-9 items-center justify-center rounded-lg text-white">
                <CheckCircle2 className="size-4" />
              </div>
              <h3 className="font-bold">{f.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- METRICS ---------------- */
function Metrics() {
  const rows = [
    { k: "No-show", before: "22–35% dos agendados", after: "Cai para 6–14% com lembrete + pré-pagamento" },
    { k: "Tempo de resposta", before: "Horas ou só no dia seguinte", after: "Segundos, 24/7, com IA Dr." },
    { k: "Conversão de orçamento", before: "Enviado e esquecido", after: "Follow-up automático em D+1, D+3, D+7" },
    { k: "Ociosidade de agenda", before: "Furos sem reagendar", after: "Lista de espera acionada por IA" },
    { k: "Custo por atendimento", before: "Alta dependência de recepção", after: "-40% em horas humanas repetitivas" },
  ];
  return (
    <section className="py-16 md:py-24">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <div className="text-sm font-semibold uppercase tracking-wide text-primary-glow">Antes vs. depois</div>
          <h2 className="mt-2 text-3xl font-bold md:text-4xl" style={{ fontFamily: "var(--font-display)" }}>
            O impacto na <span className="gradient-text">operação</span> da clínica.
          </h2>
        </div>

        <div className="mt-10 overflow-hidden rounded-2xl border border-white/10">
          <div className="grid grid-cols-3 border-b border-white/10 bg-white/[0.03] text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            <div className="p-4">Indicador</div>
            <div className="p-4">Sem Kentro</div>
            <div className="p-4 text-primary-glow">Com Kentro</div>
          </div>
          {rows.map((r, i) => (
            <div key={r.k} className={`grid grid-cols-3 text-sm ${i % 2 ? "bg-white/[0.02]" : ""}`}>
              <div className="p-4 font-semibold">{r.k}</div>
              <div className="p-4 text-muted-foreground">{r.before}</div>
              <div className="p-4 text-foreground/90">{r.after}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- TESTIMONIALS ---------------- */
function Testimonials() {
  return (
    <section className="border-y border-white/5 bg-white/[0.02] py-16 md:py-20">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl font-bold md:text-4xl" style={{ fontFamily: "var(--font-display)" }}>
          Quem opera com a Kentro, <span className="gradient-text">conta.</span>
        </h2>
        <div className="mt-8 grid gap-4 md:grid-cols-3">
          {CLINICAS_VERTICALS.map((v, i) => (
            <motion.figure
              key={v.slug}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.35, delay: i * 0.05 }}
              className="glass-card flex h-full flex-col rounded-2xl p-6"
            >
              <Quote className="size-6 text-primary-glow/80" />
              <blockquote className="mt-3 flex-1 text-sm leading-relaxed text-foreground/90">
                "{v.testimonial.quote}"
              </blockquote>
              <figcaption className="mt-4 border-t border-white/5 pt-3">
                <div className="text-sm font-semibold">{v.testimonial.author}</div>
                <div className="text-xs text-muted-foreground">{v.testimonial.role}</div>
                <div className="mt-2 inline-flex rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                  {v.name}
                </div>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- FAQ ---------------- */
function Faq() {
  return (
    <section className="py-16 md:py-24">
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
        <div className="text-sm font-semibold uppercase tracking-wide text-primary-glow">Perguntas frequentes</div>
        <h2 className="mt-2 text-3xl font-bold md:text-4xl" style={{ fontFamily: "var(--font-display)" }}>
          Dúvidas comuns de <span className="gradient-text">gestores de clínica.</span>
        </h2>
        <div className="mt-8 space-y-3">
          {CLINICAS_FAQ.map((f) => (
            <details key={f.q} className="group rounded-xl border border-white/10 bg-white/[0.02] p-4 open:bg-white/[0.04]">
              <summary className="cursor-pointer list-none font-semibold">
                {f.q}
                <span className="float-right text-muted-foreground group-open:hidden">+</span>
                <span className="float-right hidden text-muted-foreground group-open:inline">−</span>
              </summary>
              <p className="mt-3 text-sm text-muted-foreground">{f.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- FINAL CTA ---------------- */
function FinalCta() {
  return (
    <section className="py-16 md:py-24">
      <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
        <div className="glass-card rounded-3xl p-8 md:p-12">
          <h2 className="text-3xl font-bold md:text-4xl" style={{ fontFamily: "var(--font-display)" }}>
            Pronto para tirar a agenda da sua clínica <span className="gradient-text">do improviso?</span>
          </h2>
          <p className="mt-3 text-muted-foreground">
            Em 20 minutos mostramos como a Kentro se encaixa no dia a dia da sua clínica —
            médica, odontológica ou estética.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Button asChild size="lg" className="gradient-primary text-white shadow-glow">
              <Link
                to="/contato"
                search={{ src: "clinicas" }}
                onClick={() => {
                  trackEvent("demo_cta_click", {
                    location: "clinicas_final_cta",
                    page: "clinicas",
                    target: "form_contato",
                  });
                  trackMetaPixelCustom("ScheduleDemo", {
                    source: "clinicas",
                    location: "clinicas_final_cta",
                    page: "clinicas",
                  });
                }}
              >
                Agendar demonstração
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline">
              <a
                href={KENTRO.whatsappComercialUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  trackEvent("whatsapp_click", {
                    location: "clinicas_final_cta",
                    page: "clinicas",
                    number: "comercial",
                  });
                  trackMetaPixel("Contact", {
                    location: "clinicas_final_cta",
                    page: "clinicas",
                    number: "comercial",
                  });
                }}
              >
                <MessageCircle className="mr-1 size-4" /> Falar no WhatsApp
              </a>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
